import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import {
  GameTypeForm,
  emptyGameTypeForm,
  type GameTypeFormValue,
} from '../components/gameForms'

export interface GameType {
  gameTypeId: number
  gameType: string
  hasGameHubStructure: boolean
  hasSportsbookHubStructure: boolean
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
  subTypeCount: number
}

interface GameTypePage {
  gameTypes: GameType[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * GameTypesPage is the platform game-type catalogue —
 * [Catalog].[Platform.GameType] in the BsConnekto database.
 *
 * Two things drive the UI here. The id is NOT generated: the table has no
 * IDENTITY and every service that stores a GameTypeID depends on the value, so
 * creating a type asks for its id and editing never changes it. And retiring a
 * type does not cascade to its subtypes — the subtype count is shown so an
 * operator can see what else is affected.
 *
 * The HasGameHubStructure / HasSportsbookHubStructure columns are not shown:
 * they are platform wiring rather than catalogue data. Writes still carry their
 * current values, so editing a type here cannot clear them.
 */
export function GameTypesPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<GameType | null>(null)
  const [form, setForm] = useState<GameTypeFormValue>(emptyGameTypeForm)
  const [error, setError] = useState('')

  const params = { search, includeDisabled: String(includeDisabled), offset, limit: PAGE_SIZE }

  const { data, isLoading } = useQuery({
    queryKey: ['game-types', params],
    queryFn: async (): Promise<GameTypePage> => {
      const res = await api.get('/game-types', { params })
      return res.data as GameTypePage
    },
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['game-types'] })
    // A retired or renamed type changes what the subtypes list shows.
    qc.invalidateQueries({ queryKey: ['game-subtypes'] })
  }

  const openCreate = () => {
    setEditing(null)
    setForm(emptyGameTypeForm)
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (g: GameType) => {
    setEditing(g)
    setForm({
      gameTypeId: String(g.gameTypeId),
      gameType: g.gameType,
      hasGameHubStructure: g.hasGameHubStructure,
      hasSportsbookHubStructure: g.hasSportsbookHubStructure,
    })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        gameType: form.gameType.trim(),
        hasGameHubStructure: form.hasGameHubStructure,
        hasSportsbookHubStructure: form.hasSportsbookHubStructure,
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/game-types/${editing.gameTypeId}`, body)
        : api.post('/game-types', { ...body, gameTypeId: Number(form.gameTypeId) })
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('gameTypes.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (g: GameType) =>
      g.isDisabledByPlatform
        ? api.post(`/game-types/${g.gameTypeId}/restore`)
        : api.delete(`/game-types/${g.gameTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('gameTypes.errors.save'))),
  })

  const gameTypes = data?.gameTypes ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('gameTypes.title')}
        subtitle={t('gameTypes.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('gameTypes.newGameType')}
                  </Button>
                }
              />
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('gameTypes.editGameType') : t('gameTypes.newGameType')}
                  </DialogTitle>
                </DialogHeader>
                <GameTypeForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  isCreate={editing === null}
                  pending={save.isPending}
                  error={error}
                  submitLabel={editing ? t('common.save') : t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="game-types">
        <p>{t('gameTypes.help.line1')}</p>
        <p>{t('gameTypes.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('gameTypes.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      />

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('gameTypes.columns.id')}</TableHead>
                  <TableHead>{t('gameTypes.columns.name')}</TableHead>
                  <TableHead className="w-28">{t('gameTypes.columns.subTypes')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {gameTypes.map((g) => (
                  <TableRow key={g.gameTypeId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {g.gameTypeId}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link
                        to={`/game-types/${g.gameTypeId}`}
                        className="text-primary underline underline-offset-2"
                      >
                        {g.gameType}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/game-types/${g.gameTypeId}?tab=subtypes`}
                        className="text-primary underline underline-offset-2"
                      >
                        {g.subTypeCount}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={g.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(g)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={g.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(g)}
                          >
                            {g.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {gameTypes.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('gameTypes.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('gameTypes.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
